'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { getMediosGrouped, getMediosSorted, type MedioData } from '@/data/medios';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.neotral.org';

interface ArticleCount {
  source: string;
  count: number;
}

function BiasSpectrum() {
  const medios = getMediosSorted();
  
  return (
    <div className="mb-10 opacity-0 animate-fade-in" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
      <h3 className="text-xs font-mono uppercase tracking-wider text-stone-400 mb-4">
        Espectro Político de los Medios
      </h3>
      
      {/* Barra de espectro */}
      <div className="relative mb-8">
        <div className="h-4 rounded-full bg-gradient-to-r from-red-500 via-stone-300 to-blue-500 shadow-inner" />
        
        {/* Marcadores de medios */}
        <div className="relative h-16 mt-2">
          {medios.map((medio, i) => {
            // Convertir biasScore (-100 a +100) a posición (0% a 100%)
            const position = ((medio.biasScore + 100) / 200) * 100;
            // Alternar altura para evitar solapamiento
            const row = i % 2;
            
            return (
              <Link
                key={medio.id}
                href={`/medios/${medio.id}`}
                className="absolute transform -translate-x-1/2 group"
                style={{ left: `${position}%`, top: row === 0 ? '0' : '28px' }}
              >
                <div className="flex flex-col items-center">
                  <div className={`w-2 h-2 rounded-full ${
                    medio.biasScore < -20 ? 'bg-red-500' :
                    medio.biasScore > 20 ? 'bg-blue-500' :
                    'bg-stone-400'
                  } group-hover:scale-150 transition-transform`} />
                  <span className="text-[10px] text-stone-500 whitespace-nowrap mt-1 group-hover:text-stone-900 group-hover:font-semibold transition-all">
                    {medio.initials}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
        
        {/* Labels */}
        <div className="flex justify-between text-xs font-medium mt-2">
          <span className="text-red-600 flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-red-500" />
            Izquierda
          </span>
          <span className="text-stone-500">Centro</span>
          <span className="text-blue-600 flex items-center gap-1">
            Derecha
            <span className="w-2 h-2 rounded-full bg-blue-500" />
          </span>
        </div>
      </div>
      
      {/* Leyenda interactiva */}
      <p className="text-xs text-stone-400 text-center italic">
        Haz clic en cualquier punto del espectro para ver el perfil del medio
      </p>
    </div>
  );
}

function MedioCard({ 
  medio, 
  articleCount, 
  index,
  expanded,
  onToggle
}: { 
  medio: MedioData; 
  articleCount: number;
  index: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  const borderColor = medio.color === 'red' ? 'border-l-red-500 hover:border-l-red-600' : 
                      medio.color === 'blue' ? 'border-l-blue-500 hover:border-l-blue-600' : 
                      'border-l-stone-400 hover:border-l-stone-500';
  
  const biasTextColor = medio.color === 'red' ? 'text-red-600' : 
                        medio.color === 'blue' ? 'text-blue-600' : 
                        'text-stone-500';
  
  const biasBgColor = medio.color === 'red' ? 'bg-red-50' : 
                      medio.color === 'blue' ? 'bg-blue-50' : 
                      'bg-stone-100';

  return (
    <article 
      className={`group bg-white border-l-4 ${borderColor} rounded-r-lg shadow-sm hover:shadow-md transition-all duration-300 ease-out opacity-0 animate-fade-in`}
      style={{ animationDelay: `${index * 60}ms`, animationFillMode: 'forwards' }}
    >
      <div className="p-5">
        {/* Header con logo e info */}
        <div className="flex items-start gap-4">
          {/* Logo */}
          <Link href={`/medios/${medio.id}`}>
            <div className={`${medio.logoColor} w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0 shadow-inner hover:scale-105 transition-transform`}>
              <span className="text-white font-bold text-sm tracking-tight">
                {medio.initials}
              </span>
            </div>
          </Link>
          
          <div className="flex-1 min-w-0">
            <Link href={`/medios/${medio.id}`}>
              <h3 className="font-serif font-bold text-lg text-stone-900 hover:text-stone-600 transition-colors truncate">
                {medio.name}
              </h3>
            </Link>
            <p className="text-sm text-stone-500 line-clamp-2 mt-1" style={{ fontFamily: 'Georgia, serif' }}>
              {medio.tagline}
            </p>
            
            {/* Quick facts */}
            <div className="flex flex-wrap gap-2 mt-2">
              <span className="text-xs text-stone-400 bg-stone-50 px-2 py-0.5 rounded">
                📅 {medio.founded.year}
              </span>
              <span className="text-xs text-stone-400 bg-stone-50 px-2 py-0.5 rounded">
                🏢 {medio.ownership.group}
              </span>
            </div>
          </div>
        </div>
        
        {/* Footer con sesgo y acciones */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-stone-100">
          <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${biasBgColor} ${biasTextColor}`}>
            <span className={`w-2 h-2 rounded-full ${
              medio.color === 'red' ? 'bg-red-500' : 
              medio.color === 'blue' ? 'bg-blue-500' : 
              'bg-stone-400'
            }`} />
            {medio.biasLabel}
          </span>
          
          <div className="flex items-center gap-3">
            <span className="text-xs font-mono text-stone-400 bg-stone-50 px-2 py-1 rounded">
              {articleCount > 0 ? (
                <>{articleCount} art.</>
              ) : (
                <span className="animate-pulse">···</span>
              )}
            </span>
            <button
              onClick={(e) => {
                e.preventDefault();
                onToggle();
              }}
              className="text-xs text-stone-400 hover:text-stone-600 flex items-center gap-1"
            >
              {expanded ? 'Menos' : 'Más info'}
              <svg 
                className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} 
                fill="none" viewBox="0 0 24 24" stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      
      {/* Panel expandible con info detallada */}
      {expanded && (
        <div className="px-5 pb-5 border-t border-stone-100 bg-stone-50/50 animate-fade-in">
          <div className="grid gap-4 mt-4">
            {/* Propiedad */}
            <div>
              <h4 className="text-xs font-mono uppercase tracking-wider text-stone-400 mb-1.5">
                🏛️ Propiedad
              </h4>
              <p className="text-sm text-stone-700">
                <span className="font-semibold">{medio.ownership.group}</span>
                <span className="text-stone-400"> · </span>
                <span className="text-stone-500 capitalize">{medio.ownership.type}</span>
              </p>
              <p className="text-xs text-stone-500 mt-1">
                {medio.ownership.owners.slice(0, 3).join(', ')}
                {medio.ownership.owners.length > 3 && '...'}
              </p>
              {medio.ownership.notes && (
                <p className="text-xs text-stone-400 mt-1 italic">{medio.ownership.notes}</p>
              )}
            </div>
            
            {/* Línea editorial */}
            <div>
              <h4 className="text-xs font-mono uppercase tracking-wider text-stone-400 mb-1.5">
                📰 Línea editorial
              </h4>
              <p className="text-sm text-stone-600 leading-relaxed">
                {medio.editorial.summary}
              </p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {medio.editorial.values.slice(0, 4).map(value => (
                  <span key={value} className="text-xs bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full">
                    {value}
                  </span>
                ))}
              </div>
            </div>
            
            {/* Audiencia */}
            <div>
              <h4 className="text-xs font-mono uppercase tracking-wider text-stone-400 mb-1.5">
                👥 Audiencia típica
              </h4>
              <p className="text-sm text-stone-600">{medio.audience.profile}</p>
              <p className="text-xs text-stone-400 mt-1">
                {medio.audience.reach}
              </p>
            </div>
            
            {/* Transparencia */}
            <div className="flex items-center gap-4 text-xs">
              <span className="text-stone-400">Transparencia:</span>
              <span className={`font-medium ${
                medio.funding.transparency === 'alta' ? 'text-green-600' :
                medio.funding.transparency === 'media' ? 'text-amber-600' :
                medio.funding.transparency === 'baja' ? 'text-orange-600' :
                'text-red-600'
              }`}>
                {medio.funding.transparency === 'alta' ? '🟢 Alta' :
                 medio.funding.transparency === 'media' ? '🟡 Media' :
                 medio.funding.transparency === 'baja' ? '🟠 Baja' :
                 '🔴 Opaca'}
              </span>
            </div>
          </div>
        </div>
      )}
      
      {/* Ver perfil completo */}
      <Link href={`/medios/${medio.id}`}>
        <div className="px-5 py-2.5 bg-stone-50 rounded-br-lg border-t border-stone-100 hover:bg-stone-100 transition-colors">
          <span className="text-xs text-stone-500 hover:text-stone-700 flex items-center gap-1 transition-colors">
            Ver perfil completo y artículos
            <svg className="w-3.5 h-3.5 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </span>
        </div>
      </Link>
    </article>
  );
}

function StatsOverview({ counts, total }: { counts: ArticleCount[]; total: number }) {
  const grouped = getMediosGrouped();
  
  const leftIds = grouped.izquierda.map(m => m.id);
  const centerIds = grouped.centro.map(m => m.id);
  const rightIds = grouped.derecha.map(m => m.id);
  
  const leftCount = counts.filter(c => leftIds.includes(c.source)).reduce((acc, c) => acc + c.count, 0);
  const centerCount = counts.filter(c => centerIds.includes(c.source)).reduce((acc, c) => acc + c.count, 0);
  const rightCount = counts.filter(c => rightIds.includes(c.source)).reduce((acc, c) => acc + c.count, 0);

  return (
    <div className="grid grid-cols-3 gap-4 mb-10 opacity-0 animate-fade-in" style={{ animationDelay: '150ms', animationFillMode: 'forwards' }}>
      <div className="bg-red-50 border border-red-100 rounded-lg p-4 text-center">
        <p className="text-2xl font-bold text-red-600">{leftCount}</p>
        <p className="text-xs text-red-500 mt-1">Izquierda ({grouped.izquierda.length} medios)</p>
      </div>
      <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 text-center">
        <p className="text-2xl font-bold text-stone-600">{centerCount}</p>
        <p className="text-xs text-stone-500 mt-1">Centro ({grouped.centro.length} medios)</p>
      </div>
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-center">
        <p className="text-2xl font-bold text-blue-600">{rightCount}</p>
        <p className="text-xs text-blue-500 mt-1">Derecha ({grouped.derecha.length} medios)</p>
      </div>
    </div>
  );
}

function TransparencyLegend() {
  return (
    <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 mb-8 opacity-0 animate-fade-in" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
      <h4 className="text-xs font-mono uppercase tracking-wider text-stone-400 mb-3">
        Guía de transparencia
      </h4>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <div className="flex items-center gap-2">
          <span>🟢</span>
          <span className="text-stone-600"><strong>Alta:</strong> Publica informes financieros</span>
        </div>
        <div className="flex items-center gap-2">
          <span>🟡</span>
          <span className="text-stone-600"><strong>Media:</strong> Accionariado público</span>
        </div>
        <div className="flex items-center gap-2">
          <span>🟠</span>
          <span className="text-stone-600"><strong>Baja:</strong> Info parcial</span>
        </div>
        <div className="flex items-center gap-2">
          <span>🔴</span>
          <span className="text-stone-600"><strong>Opaca:</strong> Sin transparencia</span>
        </div>
      </div>
    </div>
  );
}

export default function MediosPage() {
  const [articleCounts, setArticleCounts] = useState<ArticleCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalArticles, setTotalArticles] = useState(0);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  
  const today = format(new Date(), "EEEE, d 'de' MMMM 'de' yyyy", { locale: es });
  const todayCapitalized = today.charAt(0).toUpperCase() + today.slice(1);
  
  const { izquierda, centro, derecha } = getMediosGrouped();

  useEffect(() => {
    async function fetchArticleCounts() {
      try {
        const res = await fetch(`${API_URL}/api/articles?limit=500`);
        if (!res.ok) throw new Error('API error');
        
        const data = await res.json();
        const articles = data.articles || [];
        
        const countMap: Record<string, number> = {};
        articles.forEach((a: any) => {
          countMap[a.source_id] = (countMap[a.source_id] || 0) + 1;
        });
        
        const counts = Object.entries(countMap).map(([source, count]) => ({
          source,
          count
        }));
        
        setArticleCounts(counts);
        setTotalArticles(articles.length);
      } catch (error) {
        console.error('Error fetching article counts:', error);
      } finally {
        setLoading(false);
      }
    }
    
    fetchArticleCounts();
  }, []);

  const getCountForSource = (sourceId: string) => {
    const found = articleCounts.find(c => c.source === sourceId);
    return found?.count || 0;
  };
  
  const toggleCard = (id: string) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-[#FDF6E3]">
      {/* Header */}
      <header className="border-b-2 border-stone-800 bg-[#FDF6E3] sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          {/* Top bar: Date */}
          <div className="flex items-center justify-between py-2 border-b border-stone-200">
            <span className="text-xs text-stone-500 font-mono uppercase tracking-wider">
              {todayCapitalized}
            </span>
            <span className="text-xs text-stone-400">España</span>
          </div>
          
          {/* Masthead */}
          <div className="text-center py-4">
            <Link href="/">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-black tracking-tight text-stone-900 hover:text-stone-700 transition-colors">
                NEOTRAL
              </h1>
            </Link>
            <p className="text-xs sm:text-sm text-stone-500 italic mt-1 font-serif">
              Todas las versiones. Tú decides.
            </p>
          </div>
          
          {/* Navigation */}
          <nav className="flex justify-center gap-6 sm:gap-8 py-2 border-t border-stone-200 text-sm overflow-x-auto no-scrollbar">
            <Link href="/" className="font-serif text-stone-500 hover:text-stone-900 transition-colors whitespace-nowrap">
              Hoy
            </Link>
            <Link href="/radar" className="font-serif text-stone-500 hover:text-stone-900 transition-colors whitespace-nowrap">
              Radar
            </Link>
            <Link href="/medios" className="font-serif font-semibold text-stone-900 whitespace-nowrap">
              Medios
            </Link>
            <Link href="/silenciadas" className="font-serif text-stone-500 hover:text-stone-900 transition-colors whitespace-nowrap">
              Silenciadas
            </Link>
            <Link href="/algoritmo" className="font-serif text-stone-500 hover:text-stone-900 transition-colors whitespace-nowrap">
              Algoritmo
            </Link>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {/* Page Header */}
        <div className="mb-8 opacity-0 animate-fade-in" style={{ animationFillMode: 'forwards' }}>
          <h2 className="text-3xl sm:text-4xl font-serif font-bold text-stone-900 mb-3">
            📰 Mapa de Medios Españoles
          </h2>
          <p className="text-lg text-stone-600 max-w-3xl" style={{ fontFamily: 'Georgia, serif' }}>
            Conoce quién está detrás de cada medio, su línea editorial, su historia y su audiencia. 
            La transparencia sobre la prensa es el primer paso para una ciudadanía informada.
          </p>
          {totalArticles > 0 && (
            <p className="text-sm text-stone-500 mt-2 font-mono">
              Analizando {totalArticles} artículos de {izquierda.length + centro.length + derecha.length} medios
            </p>
          )}
        </div>

        {/* Espectro político visual mejorado */}
        <BiasSpectrum />

        {/* Stats Overview */}
        {articleCounts.length > 0 && (
          <StatsOverview counts={articleCounts} total={totalArticles} />
        )}
        
        {/* Leyenda de transparencia */}
        <TransparencyLegend />

        {/* Grid de medios por sección */}
        <section className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-3 h-3 rounded-full bg-red-500" />
            <h3 className="text-sm font-mono uppercase tracking-wider text-red-600">
              Izquierda / Centro-izquierda
            </h3>
            <div className="flex-1 h-px bg-red-200" />
            <span className="text-xs text-stone-400">{izquierda.length} medios</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {izquierda.map((medio, i) => (
              <MedioCard 
                key={medio.id} 
                medio={medio} 
                articleCount={getCountForSource(medio.id)}
                index={i}
                expanded={expandedCards.has(medio.id)}
                onToggle={() => toggleCard(medio.id)}
              />
            ))}
          </div>
        </section>

        <section className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-3 h-3 rounded-full bg-stone-400" />
            <h3 className="text-sm font-mono uppercase tracking-wider text-stone-500">
              Centro
            </h3>
            <div className="flex-1 h-px bg-stone-200" />
            <span className="text-xs text-stone-400">{centro.length} medios</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {centro.map((medio, i) => (
              <MedioCard 
                key={medio.id} 
                medio={medio} 
                articleCount={getCountForSource(medio.id)}
                index={i + izquierda.length}
                expanded={expandedCards.has(medio.id)}
                onToggle={() => toggleCard(medio.id)}
              />
            ))}
          </div>
        </section>

        <section className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-3 h-3 rounded-full bg-blue-500" />
            <h3 className="text-sm font-mono uppercase tracking-wider text-blue-600">
              Centro-derecha / Derecha
            </h3>
            <div className="flex-1 h-px bg-blue-200" />
            <span className="text-xs text-stone-400">{derecha.length} medios</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {derecha.map((medio, i) => (
              <MedioCard 
                key={medio.id} 
                medio={medio} 
                articleCount={getCountForSource(medio.id)}
                index={i + izquierda.length + centro.length}
                expanded={expandedCards.has(medio.id)}
                onToggle={() => toggleCard(medio.id)}
              />
            ))}
          </div>
        </section>

        {/* Callout informativo mejorado */}
        <div className="bg-amber-50/70 border border-amber-200 rounded-lg p-6 mt-10 opacity-0 animate-fade-in" style={{ animationDelay: '600ms', animationFillMode: 'forwards' }}>
          <div className="flex items-start gap-3">
            <span className="text-2xl">📊</span>
            <div>
              <h3 className="font-serif font-semibold text-amber-900 mb-2">
                Metodología de clasificación
              </h3>
              <p className="text-amber-800 text-sm leading-relaxed mb-3" style={{ fontFamily: 'Georgia, serif' }}>
                La clasificación de sesgo político se basa en múltiples factores: línea editorial histórica, 
                cobertura de temas polémicos, afinidad con partidos políticos, y estudios académicos de sesgo 
                mediático en España (Reuters Institute, CIS, etc.).
              </p>
              <p className="text-amber-700 text-sm leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
                <strong>Importante:</strong> Ningún medio es completamente objetivo. Esta clasificación es 
                orientativa y está sujeta a debate. Te animamos a leer medios de todo el espectro para 
                formar tu propia opinión.
              </p>
            </div>
          </div>
        </div>
        
        {/* Recursos adicionales */}
        <div className="bg-stone-50 border border-stone-200 rounded-lg p-6 mt-6 opacity-0 animate-fade-in" style={{ animationDelay: '700ms', animationFillMode: 'forwards' }}>
          <h3 className="font-serif font-semibold text-stone-800 mb-3">
            🔗 Para profundizar
          </h3>
          <ul className="text-sm text-stone-600 space-y-2" style={{ fontFamily: 'Georgia, serif' }}>
            <li>
              <a href="https://rfrp.uocquaderns.com/" target="_blank" rel="noopener" className="text-stone-700 hover:text-stone-900 underline">
                Ranking de Fiabilidad de Reuters Institute
              </a>
              {' '}— Estudio anual sobre confianza en medios
            </li>
            <li>
              <a href="https://www.apmadrid.es/publicaciones/" target="_blank" rel="noopener" className="text-stone-700 hover:text-stone-900 underline">
                Asociación de la Prensa de Madrid
              </a>
              {' '}— Informes sobre la profesión periodística
            </li>
            <li>
              <a href="https://maldita.es/" target="_blank" rel="noopener" className="text-stone-700 hover:text-stone-900 underline">
                Maldita.es
              </a>
              {' '}— Verificación de bulos y desinformación
            </li>
          </ul>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-stone-300 mt-12 bg-[#FDF6E3]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 text-center">
          <p className="text-xs text-stone-400 font-serif">
            © {new Date().getFullYear()} Neotral · Fundación MAFT
          </p>
        </div>
      </footer>
    </div>
  );
}
